/**
 * How wide an element actually is.
 *
 * The arced hand needs this (H2): how many cards fit on a row decides how far
 * each one tilts, and that is a measurement rather than a guess. Media queries
 * cannot answer it, because the hand is not the viewport - it sits inside
 * padding, and on a television inside a column.
 *
 * Reports 0 until the first observation, which callers treat as "not measured
 * yet" rather than "no room". Guessing a width instead would draw the wrong
 * number of rows on the first frame and visibly re-flow on the second.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function useWidth<T extends HTMLElement>(): [(node: T | null) => void, number] {
  const [width, setWidth] = useState(0);
  const observed = useRef<T | null>(null);
  const observer = useRef<ResizeObserver | null>(null);

  /*
   * A callback ref rather than an object ref plus an effect.
   *
   * The element this measures is rendered conditionally - a zone with no cards
   * draws something else entirely - so it appears and disappears under a
   * mounted component. An effect keyed on nothing would observe whatever was
   * there on mount and never notice the swap; a callback ref fires on every
   * change of the node itself, which is the actual event of interest.
   */
  const ref = useCallback((node: T | null) => {
    observed.current = node;

    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
    if (!node) return;

    // `borderBoxSize` would include padding the cards cannot use; the content
    // box is the room they actually have.
    const next = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    next.observe(node);
    observer.current = next;

    setWidth(node.getBoundingClientRect().width);
  }, []);

  // Unmounting the component, rather than the node, still has to let go.
  useEffect(() => {
    return () => {
      observer.current?.disconnect();
      observer.current = null;
    };
  }, []);

  return [ref, width];
}
